import { Global } from '@emotion/react';
import styled from '@emotion/styled';
import type { ReactNode } from 'react';
import React, { forwardRef, useMemo } from 'react';

import { useComponentTheme } from '../useComponentTheme';
import { withSpacingStyles } from '../withSpacingStyles';

type Props = {
  align?: 'center' | 'left' | 'right',
  backgroundColor?: string,
  backgroundCover?: boolean,
  backgroundImage?: string,
  borderRadius?: string,
  boxShadowDisplay?: boolean,
  children: ReactNode,
  className?: string,
  isHidden: boolean,
  isLoading: boolean,
  margin?: string,
  mode?: string,
  title?: ReactNode,
};

const LayoutCard = withSpacingStyles(
  forwardRef(
    (
      {
        align,
        backgroundColor,
        backgroundCover,
        backgroundImage,
        borderRadius,
        boxShadowDisplay,
        children,
        className,
        isHidden,
        isLoading,
        margin,
        mode,
        title,
        ...rest
      }: Props,
      ref
    ) => {
      const componentTheme = useComponentTheme(theme => theme.components.LayoutCard, mode);
      const hasTitle = !!title && !isLoading;

      const customisedComponentTheme = useMemo(
        () =>
          isLoading
            ? { backgroundColor: 'transparent', backgroundCover: true, padding: componentTheme.padding }
            : {
                ...componentTheme,
                align: align ?? componentTheme.align,
                backgroundColor: backgroundColor ?? componentTheme.backgroundColor,
                backgroundCover: backgroundCover ?? componentTheme.backgroundCover,
                backgroundImage: backgroundImage ?? componentTheme.backgroundImage,
                borderRadius: borderRadius ?? componentTheme.borderRadius,
                boxShadowDisplay: boxShadowDisplay ?? componentTheme.boxShadowDisplay,
                margin: margin ?? componentTheme.margin,
              },
        [
          align,
          backgroundColor,
          backgroundCover,
          backgroundImage,
          borderRadius,
          boxShadowDisplay,
          componentTheme,
          isLoading,
          margin,
        ]
      );

      const hasBoxProperties = !!(
        isLoading ||
        customisedComponentTheme.backgroundColor ||
        customisedComponentTheme.backgroundImage ||
        customisedComponentTheme.border ||
        customisedComponentTheme.boxShadowDisplay
      );

      return (
        <>
          <Global
            styles={{
              '[data-background-cover="true"] + [data-background-cover="true"]': {
                marginTop: 'calc(var(--row-gap, 0) * -1)',
              },
            }}
          />

          <Section
            className={className}
            componentTheme={customisedComponentTheme}
            data-background-cover={!!customisedComponentTheme.backgroundCover}
            hasBoxProperties={hasBoxProperties}
            hasTitle={hasTitle}
            isHidden={isHidden}
            ref={ref}
            {...rest}
          >
            {hasTitle && <Title componentTheme={customisedComponentTheme}>{title}</Title>}

            <Content
              componentTheme={customisedComponentTheme}
              data-test="layout-card-content"
              hasBoxProperties={hasBoxProperties}
              hasTitle={hasTitle}
            >
              {children}
            </Content>
          </Section>
        </>
      );
    }
  )
);

const addStyles = (condition, styles) => (condition ? styles : '');

const getSpacing = (type, value) => {
  const span = document.createElement('span');

  span.style[type] = value;
  span.hidden = true;
  document.body.appendChild(span);

  const computedStyles = window.getComputedStyle(span);

  const spacingValues = {
    [`${type}Bottom`]: computedStyles.getPropertyValue(`${type}-bottom`),
    [`${type}Left`]: computedStyles.getPropertyValue(`${type}-left`),
    [`${type}Right`]: computedStyles.getPropertyValue(`${type}-right`),
    [`${type}Top`]: computedStyles.getPropertyValue(`${type}-top`),
  };

  span.remove();

  return spacingValues;
};

const FullWidthLayoutCardContent = styled.div(({ hasHorizontallScrolling }) =>
  hasHorizontallScrolling
    ? `
    --padding: 8px;
    /* Add this padding and margin so that box shadows on child elements don't get cut off by the overflow-x, and so
      that the content scrolls off the edge of the page for articles with a horizontal direction.
      This method won't work when the margin or padding has a percentage value. */
    padding:
      var(--padding)
      calc(var(--margin-left) + var(--padding-left))
      var(--padding)
      calc(var(--margin-right) + var(--padding-right));

    margin:
      calc(var(--padding) * -1)
      calc((var(--margin-left) + var(--padding-left)) * -1)
      calc(var(--padding) * -1)
      calc((var(--margin-right) + var(--padding-right)) * -1);

    overflow-x: auto;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  `
    : 'width: 100%;'
);

const Section = styled.section(
  ({
    componentTheme: {
      boxShadowDisplay,
      backgroundColor,
      backgroundCover,
      backgroundImage,
      border,
      borderRadius,
      margin,
      padding,
    },
    hasBoxProperties,
    hasTitle,
    isHidden,
    theme,
  }) => {
    const { paddingLeft, paddingRight } = getSpacing('padding', padding);
    const { marginLeft, marginRight } = getSpacing('margin', margin);

    return `
      --margin-right: ${marginRight || '0px'};
      --margin-left: ${marginLeft || '0px'};
      --padding-right: ${hasBoxProperties && paddingRight ? paddingRight : '0px'};
      --padding-left: ${hasBoxProperties && paddingLeft ? paddingLeft : '0px'};

      display: ${isHidden ? 'none' : 'grid'};
      grid-template-columns: 100%;
      position: relative;
      width: calc(100% - var(--margin-left) - var(--margin-right));
      ${addStyles(backgroundCover && hasBoxProperties && padding, `padding: ${padding};`)}
      ${addStyles(margin, `margin: ${margin};`)}

      &::before {
        ${addStyles(!backgroundCover && hasTitle, `grid-row: 2;`)}
        position: absolute;
        top: 0;
        left: 0;
        z-index: -1;
        width: 100%;
        height: 100%;
        content: '';
        ${addStyles(border, `border: ${border};`)}
        border-radius: ${borderRadius ?? theme.borderRadius.large};
        ${addStyles(boxShadowDisplay, `box-shadow: ${theme.boxShadow.small};`)}
        ${addStyles(backgroundImage, `background: url(${backgroundImage}) center / cover no-repeat;`)}
        ${addStyles(backgroundColor, `background-color: ${backgroundColor};`)}
      }
    `;
  }
);

const Content = styled.div(({ componentTheme: { align, backgroundCover, padding }, hasBoxProperties, hasTitle }) => {
  const alignStyles = {
    center: 'align-items: center; text-align: center;',
    left: 'align-items: flex-start; text-align: left;',
    right: 'align-items: flex-end; text-align: right;',
  };

  return `
    width: 100%;
    ${addStyles(!backgroundCover && hasTitle, `grid-row: 2;`)}
    ${addStyles(!backgroundCover && hasBoxProperties && padding, `padding: ${padding};`)}
    ${addStyles(align, `display: flex; flex-direction: column; ${alignStyles[align]}`)}
  `;
});

const Title = styled.div(
  ({ componentTheme: { backgroundCover } }) => `
  width: 100%;
  ${!backgroundCover ? `grid-row: 1; ` : ''}
`
);

export { FullWidthLayoutCardContent, LayoutCard };
